import React, { useState, useEffect } from "react";
import { Modal, Form, Input, notification, Row, Col } from "antd";
import Joi from "joi";
import axios from "axios";

const Create = ({ isVisible, handleClose, moduleName, base_url, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});

  const joiOptions = { abortEarly: false };

  // ------------------- VALIDATION -------------------
  const validate = (value) => {
    const schemaObj = {
      tagline: Joi.string().required().label("Tagline"),
      msg: Joi.string().required().label("Message"),
      name: Joi.string().required().label("Name"),
      desg: Joi.string().allow("").label("Designation"),
    };

    const schema = Joi.object(schemaObj).options({ allowUnknown: true });
    const { error } = schema.validate(value, joiOptions);

    const errors = {};
    if (error) {
      error.details.forEach((field) => {
        errors[field.path[0]] = field.message;
      });
    }
    return errors;
  };

  // ------------------- PRE-FILL FORM -------------------
  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        tagline: record.tagline,
        msg: record.msg,
        name: record.name,
        desg: record.desg || "",
      });
    } else {
      form.resetFields();
    }
  }, [record, form]);

  // ------------------- SUBMIT -------------------
  const handleSubmit = async (value) => {
    const errors = validate(value);
    setFormError(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    try {
      const apiEndpoint = record
        ? `${base_url}updateTeamVoices` // must match backend
        : `${base_url}addTeamVoices`; // must match backend

      const payload = record ? { ...value, id: record._id } : value;

      const response = await axios.post(apiEndpoint, payload);

      if (response?.data?.message) {
        notification.success({
          message: response.data.message,
        });
        handleClose(true); // refresh list
      } else {
        notification.error({
          message: "Failed to save record",
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: err?.response?.data?.message || "Something went wrong",
      });
    }
    setLoading(false);
  };

  return (
    <Modal
      title={record ? `Edit ${moduleName}` : `Add ${moduleName}`}
      visible={isVisible}
      onCancel={() => handleClose(false)}
      okText={record ? "Update" : "Create"}
      cancelText="Cancel"
      confirmLoading={loading}
      onOk={() => {
        form
          .validateFields()
          .then((values) => handleSubmit(values))
          .catch(() => {});
      }}
      width={700}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Tagline"
              name="tagline"
              required
              {...(formError.tagline && {
                help: formError.tagline,
                validateStatus: "error",
              })}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Name"
              name="name"
              required
              {...(formError.name && {
                help: formError.name,
                validateStatus: "error",
              })}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Designation"
              name="desg"
              {...(formError.desg && {
                help: formError.desg,
                validateStatus: "error",
              })}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label="Message"
              name="msg"
              extra="Recommended character limit: 850"
              required
              {...(formError.msg && {
                help: formError.msg,
                validateStatus: "error",
              })}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Create;
